import React, { useMemo } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as WordIcon,
  TableChart as ExcelIcon,
  Email as MsgIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius } from '../../../constants';
import { formatFileSize, isImageFile, isPdfFile, isWordFile, isExcelFile, isMsgFile } from '../../../utils/fileUtils';
import { ACCEPTED_FILE_INPUT_TYPES } from '../../../constants/raiseQueryForm';
import SectionCard from './SectionCard';
import FormFieldLabel from './FormFieldLabel';
import { getTextareaStyle, getHelperTextStyles } from '../../../utils/formUtils';

/**
 * Get the appropriate icon for a file based on its type
 */
const getFileIcon = (file) => {
  if (isImageFile(file)) return <ImageIcon sx={{ fontSize: '18px', color: '#10B981' }} />;
  if (isPdfFile(file)) return <PdfIcon sx={{ fontSize: '18px', color: '#EF4444' }} />;
  if (isWordFile(file)) return <WordIcon sx={{ fontSize: '18px', color: '#3B82F6' }} />;
  if (isExcelFile(file)) return <ExcelIcon sx={{ fontSize: '18px', color: '#10B981' }} />;
  if (isMsgFile(file)) return <MsgIcon sx={{ fontSize: '18px', color: '#F59E0B' }} />;
  return <FileIcon sx={{ fontSize: '18px', color: '#64748b' }} />;
};

/**
 * Check if a file can be previewed in the browser
 */
const canPreview = (file) => {
  return isImageFile(file) || isPdfFile(file);
};

/**
 * Open file preview in a new tab
 */
const handlePreviewFile = (file) => {
  const url = URL.createObjectURL(file);
  window.open(url, '_blank');
};

/**
 * DetailsSection Component
 * Details section with description and attachment fields.
 * Compact, mobile-friendly layout: Description on left, small upload button on right.
 * File list displayed below in a clean auto-expanding grid.
 */
const DetailsSection = ({
  formData,
  errors,
  focusedFields,
  attachments,
  isDragging,
  fileInputRef,
  attachmentInputKey,
  handleChange,
  handleFocus,
  handleBlur,
  handleDropZoneClick,
  handleDragEnter,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleRemoveAttachment,
  handleAttachmentChange,
}) => {
  const helperTextBaseStyles = getHelperTextStyles();

  const getTextareaStyleWithFocus = (field, hasError) => {
    const isFocused = focusedFields[field];
    return getTextareaStyle(field, hasError, isFocused);
  };

  // Generate image previews for image files
  const filePreviews = useMemo(() => {
    return attachments.map((file) => {
      if (isImageFile(file)) {
        return URL.createObjectURL(file);
      }
      return null;
    });
  }, [attachments]);

  // Calculate total size of all attachments
  const totalSize = useMemo(() => {
    return attachments.reduce((sum, file) => sum + file.size, 0);
  }, [attachments]);

  return (
    <SectionCard title="Details">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing.xs, width: '100%', boxSizing: 'border-box' }}>
        {/* Row: Description + Upload Button */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 120px' },
            gap: spacing.sm,
            width: '100%',
            alignItems: 'start',
            boxSizing: 'border-box',
          }}
        >
          {/* Description Field */}
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
            <FormFieldLabel label="Description" required={true} />
            <textarea
              placeholder="Provide a detailed description"
              value={formData.queryDescription}
              onChange={handleChange('queryDescription')}
              onFocus={handleFocus('queryDescription')}
              onBlur={handleBlur('queryDescription')}
              rows={3}
              style={{
                ...getTextareaStyleWithFocus('queryDescription', !!errors.queryDescription),
                width: '100%',
                boxSizing: 'border-box',
                maxWidth: '100%',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                ...helperTextBaseStyles,
                color: errors.queryDescription ? colors.error : colors.textPrimaryDark,
              }}
            >
              {errors.queryDescription}
            </Typography>
          </Box>

          {/* Compact Upload Button */}
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
            <FormFieldLabel label="Attachment" />
            <Box
              onClick={handleDropZoneClick}
              sx={{
                width: '100%',
                height: '80px',
                border: `1.5px dashed #cbd5e1`,
                borderRadius: borderRadius.md,
                backgroundColor: colors.backgroundWhite,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                boxSizing: 'border-box',
                '&:hover': {
                  borderColor: colors.primaryDark,
                  backgroundColor: colors.primarySoft,
                },
              }}
            >
              <input
                ref={fileInputRef}
                key={attachmentInputKey}
                type="file"
                multiple
                accept={ACCEPTED_FILE_INPUT_TYPES}
                onChange={handleAttachmentChange}
                style={{ display: 'none' }}
              />
              <CloudUploadIcon
                sx={{
                  fontSize: '1.5rem',
                  color: '#64748b',
                }}
              />
              <Typography
                sx={{
                  fontSize: fonts.fontSizeXs,
                  color: '#94a3b8',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                Click to upload
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* File List — shown below, auto-expanding, no scroll */}
        {attachments.length > 0 && (
          <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
            {/* Summary line */}
            <Typography
              variant="caption"
              sx={{
                fontSize: fonts.fontSizeSm,
                color: colors.textSecondary,
                fontWeight: fonts.weightMedium,
                mb: '6px',
                display: 'block',
              }}
            >
              {attachments.length} file{attachments.length !== 1 ? 's' : ''} selected • {formatFileSize(totalSize)}
            </Typography>

            {/* File grid */}
            <Box
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: '8px',
                boxSizing: 'border-box',
              }}
            >
              {attachments.map((file, index) => (
                <Box
                  key={`${file.name}-${index}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 8px',
                    backgroundColor: '#f8f9fb',
                    border: '1px solid #e5e7eb',
                    borderRadius: borderRadius.sm,
                    transition: 'all 0.15s ease-in-out',
                    minWidth: 0,
                    overflow: 'hidden',
                    '&:hover': {
                      borderColor: '#cbd5e1',
                      backgroundColor: '#f1f3f5',
                    },
                  }}
                >
                  {/* Thumbnail / Icon */}
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      overflow: 'hidden',
                    }}
                  >
                    {isImageFile(file) && filePreviews[index] ? (
                      <img
                        src={filePreviews[index]}
                        alt={file.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '5px',
                        }}
                      />
                    ) : (
                      getFileIcon(file)
                    )}
                  </Box>

                  {/* File Info */}
                  <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Tooltip title={file.name} placement="top" arrow enterDelay={400}>
                      <Typography
                        sx={{
                          fontSize: fonts.fontSizeSm,
                          fontWeight: fonts.weightMedium,
                          color: '#1e293b',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          lineHeight: 1.2,
                        }}
                      >
                        {file.name}
                      </Typography>
                    </Tooltip>
                    <Typography
                      sx={{
                        fontSize: fonts.fontSizeXs,
                        color: '#94a3b8',
                        lineHeight: 1.2,
                      }}
                    >
                      {formatFileSize(file.size)}
                    </Typography>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    {canPreview(file) && (
                      <Tooltip title="Preview" placement="top" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewFile(file);
                          }}
                          sx={{
                            padding: '3px',
                            color: '#94a3b8',
                            '&:hover': {
                              color: colors.primaryDark,
                              backgroundColor: 'rgba(35, 52, 145, 0.08)',
                            },
                          }}
                        >
                          <PreviewIcon sx={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Remove" placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveAttachment(index);
                        }}
                        sx={{
                          padding: '3px',
                          color: '#94a3b8',
                          '&:hover': {
                            color: colors.error,
                            backgroundColor: 'rgba(239, 68, 68, 0.08)',
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: '15px' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </SectionCard>
  );
};

export default DetailsSection;
